import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, MapPin, Calendar, User } from 'lucide-react';
import Badge from '../common/Badge';
import Button from '../common/Button';
import toast from 'react-hot-toast';

const BookListItem = ({ book, isAdmin = false }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const normalizedBook = {
        id: book.id,
        title: book.title || book.titre,
        author: book.author || book.auteur_nom,
        category: book.category || book.categorie_nom,
        genre: book.genre,
        publication_year: book.publication_year || book.annee_publication,
        copies_available: book.copies_available,
        location: book.location,
        status: book.status || book.statut || 'disponible',
        cover_url: book.cover_url || book.couverture_url || book.image_url || book.image_couverture || book.image,
        description: book.description || book.resume
    };

    const getStatusVariant = (statut) => {
        switch (statut) {
            case 'disponible': return 'success';
            case 'reserve_complet': return 'danger';
            case 'maintenance': return 'warning';
            default: return 'default';
        }
    };

    const getStatusLabel = (statut) => t(`book_status.${statut}`, statut);

    const handleEdit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/admin/books/${book.id}/edit`);
    };

    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toast.error('Action non disponible sur la version statique');
    };

    return (
        <Link to={isAdmin ? '#' : `/books/${book.id}`} className="group block bg-white dark:bg-dark-surface rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 dark:border-gray-800 p-4">
            <div className="flex flex-col sm:flex-row gap-6">
                {/* Cover Image */}
                <div className="flex-shrink-0 w-full sm:w-32 md:w-40 aspect-[2/3] relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                    {normalizedBook.cover_url ? (
                        <img
                            src={normalizedBook.cover_url}
                            alt={normalizedBook.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                            <BookOpen className="h-8 w-8 mb-2 opacity-50" />
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="flex-grow flex flex-col justify-between">
                    <div>
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-2 mb-2">
                            <div>
                                {(normalizedBook.category || normalizedBook.genre) && (
                                    <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wider mb-1 block">
                                        {normalizedBook.category || normalizedBook.genre}
                                    </span>
                                )}
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors mb-1">
                                    {normalizedBook.title}
                                </h3>
                                <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm mb-2">
                                    <User className="w-4 h-4 mr-1" />
                                    <span className="font-medium">{normalizedBook.author}</span>
                                </div>
                            </div>

                            <Badge variant={getStatusVariant(normalizedBook.status)} size="sm">
                                {getStatusLabel(normalizedBook.status)}
                            </Badge>
                        </div>

                        <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 md:line-clamp-3 mb-4">
                            {normalizedBook.description || t('book.no_description')}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-500 pt-4 border-t border-gray-100 dark:border-gray-800">
                        {normalizedBook.publication_year && (
                            <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1.5" />
                                <span>{normalizedBook.publication_year}</span>
                            </div>
                        )}

                        {normalizedBook.location && (
                            <div className="flex items-center">
                                <MapPin className="w-4 h-4 mr-1.5" />
                                <span>{normalizedBook.location}</span>
                            </div>
                        )}

                        {normalizedBook.status === 'disponible' && normalizedBook.copies_available && (
                            <div className="ml-auto font-medium text-green-600 dark:text-green-400">
                                {t('components.book_card.available_copies', { count: normalizedBook.copies_available })}
                            </div>
                        )}
                    </div>

                    {isAdmin && (
                        <div className="flex gap-3 mt-4 justify-end">
                            <Button size="sm" variant="outline" onClick={handleEdit}>
                                {t('common.edit')}
                            </Button>
                            <Button size="sm" variant="danger" onClick={handleDelete}>
                                {t('common.delete')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
};

export default BookListItem;
